import Tippy, { TippyProps } from '@tippyjs/react';

import React from 'react';
import { useModalLayer } from 'ud-ui/components/modal/modal-layer-context';

type TippySize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export interface UDTippyProps extends Omit<TippyProps, 'size'> {
  size?: TippySize;
}

/**
 * UDTippy - компонент тултипа с возможностью настройки размера шрифта
 * По умолчанию имеет placement="right" и theme="light"
 */
const UDTippy: React.FC<UDTippyProps> = ({
  children,
  size = 'md',
  placement = 'right',
  theme = 'light',
  trigger = 'mouseenter focus',
  ...props
}) => {
  // Внутри окна подсказка кладётся в его `<dialog>`: всё в `body` под открытым окном и инертно (IMGM-1623).
  // Явный `appendTo` побеждает.
  const { dialog } = useModalLayer();
  const appendTo = dialog ? { appendTo: dialog } : {};

  // Функция для установки класса размера
  const setSizeClass = (instance: any) => {
    const tippyBox = instance.popper?.querySelector('.tippy-box');
    if (tippyBox) {
      // Удаляем все классы размеров
      tippyBox.classList.remove(
        'tippy-size-xxs',
        'tippy-size-xs',
        'tippy-size-sm',
        'tippy-size-md',
        'tippy-size-lg',
        'tippy-size-xl',
        'tippy-size-xxl',
      );

      // Добавляем нужный класс размера
      tippyBox.classList.add(`tippy-size-${size}`);
    }
  };

  if (!props.content) {
    return children || null;
  }

  return (
    <Tippy
      placement={placement}
      theme={theme}
      trigger={trigger}
      {...appendTo}
      {...props}
      onCreate={(instance) => {
        setSizeClass(instance);

        // Вызываем оригинальный onCreate, если он был передан
        if (props.onCreate) {
          props.onCreate(instance);
        }
      }}
      onMount={(instance) => {
        setSizeClass(instance);

        // Вызываем оригинальный onMount, если он был передан
        if (props.onMount) {
          props.onMount(instance);
        }
      }}
      onShow={(instance) => {
        setSizeClass(instance);

        // Вызываем оригинальный onShow и возвращаем его результат: `false` по контракту tippy отменяет показ.
        return props.onShow?.(instance);
      }}
    >
      {children}
    </Tippy>
  );
};

export default UDTippy;
